import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Mail, RefreshCw, Settings, CheckCircle, XCircle, Send, Users,
  ListPlus, Link2, Clock, BarChart3, TrendingUp, Star, AlertTriangle,
  TrendingDown
} from 'lucide-react'
import { brevoApi, SegmentMapping } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'
import { cn } from '@/lib/utils'
import CampaignModal from '@/components/CampaignModal'
import BrevoConfigModal from '@/components/BrevoConfigModal'

// Segment styling from CustomersPage
const segmentStyles: Record<string, {
  gradient: string,
  icon: any,
  iconColor: string,
  bgLight: string,
  bgDark: string
}> = {
  'Champions': {
    gradient: 'from-green-500 to-emerald-600',
    icon: Star,
    iconColor: 'text-success-600 dark:text-success-400',
    bgLight: 'bg-green-50',
    bgDark: 'bg-green-900/20'
  },
  'Loyal': {
    gradient: 'from-blue-500 to-indigo-600',
    icon: TrendingUp,
    iconColor: 'text-primary-600 dark:text-primary-400',
    bgLight: 'bg-blue-50',
    bgDark: 'bg-blue-900/20'
  },
  'At Risk': {
    gradient: 'from-orange-500 to-red-600',
    icon: AlertTriangle,
    iconColor: 'text-orange-600 dark:text-orange-400',
    bgLight: 'bg-orange-50',
    bgDark: 'bg-orange-900/20'
  },
  'Lost': {
    gradient: 'from-red-600 to-rose-700',
    icon: TrendingDown,
    iconColor: 'text-danger-600 dark:text-danger-400',
    bgLight: 'bg-red-50',
    bgDark: 'bg-red-900/20'
  },
  'One-Timers': {
    gradient: 'from-gray-500 to-slate-600',
    icon: Users,
    iconColor: 'text-slate-600 dark:text-slate-400',
    bgLight: 'bg-slate-50',
    bgDark: 'bg-gray-900/20'
  },
  'Declining': {
    gradient: 'from-rose-600 to-red-800',
    icon: TrendingDown,
    iconColor: 'text-rose-600 dark:text-rose-400',
    bgLight: 'bg-rose-50',
    bgDark: 'bg-rose-900/20'
  },
  'Promising': {
    gradient: 'from-teal-500 to-cyan-600',
    icon: TrendingUp,
    iconColor: 'text-teal-600 dark:text-teal-400',
    bgLight: 'bg-teal-50',
    bgDark: 'bg-teal-900/20'
  }
}

export default function MarketingPage() {
  const toast = useToast()
  const queryClient = useQueryClient()

  const [showConfigModal, setShowConfigModal] = useState(false)
  const [showCampaignModal, setShowCampaignModal] = useState(false)
  const [selectedSegmentForCampaign, setSelectedSegmentForCampaign] = useState<string | null>(null)

  // Queries
  const statusQuery = useQuery({
    queryKey: ['brevo-status'],
    queryFn: brevoApi.getStatus,
    refetchInterval: 60000 // Refresh every minute
  })

  const configQuery = useQuery({
    queryKey: ['brevo-config'],
    queryFn: brevoApi.getConfig
  })

  const listsQuery = useQuery({
    queryKey: ['brevo-lists'],
    queryFn: brevoApi.getLists,
    enabled: statusQuery.data?.connected === true
  })

  const campaignsQuery = useQuery({
    queryKey: ['brevo-campaigns'],
    queryFn: () => brevoApi.getCampaigns('sent', 20),
    enabled: statusQuery.data?.connected === true
  })

  // Mutations
  const syncAllMutation = useMutation({
    mutationFn: () => brevoApi.syncAll(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['brevo-config'] })
      toast.success(`Sincronizacion completada: ${data.message}`)
    },
    onError: (error: any) => {
      toast.error('Error sincronizando', error.response?.data?.detail || error.message)
    }
  })

  const syncSegmentMutation = useMutation({
    mutationFn: (segmentName: string) => brevoApi.syncSegment(segmentName),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['brevo-config'] })
      toast.success(`Sincronizados ${data.contacts_synced} contactos del segmento "${data.segment_name}"`)
    },
    onError: (error: any) => {
      toast.error('Error sincronizando segmento', error.response?.data?.detail || error.message)
    }
  })

  const getSegmentStyle = (name: string) => segmentStyles[name] || segmentStyles['One-Timers']

  const isConnected = statusQuery.data?.connected === true

  const handleCreateCampaign = (segmentName?: string) => {
    setSelectedSegmentForCampaign(segmentName || null)
    setShowCampaignModal(true)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
            Email Marketing
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Gestiona campanas de email marketing con Brevo
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowConfigModal(true)}
            className="btn btn-secondary"
          >
            <Settings className="w-4 h-4 mr-2" />
            Configuracion
          </button>
          <button
            onClick={() => handleCreateCampaign()}
            disabled={!isConnected}
            className="btn btn-primary"
          >
            <Send className="w-4 h-4 mr-2" />
            Nueva Campana
          </button>
        </div>
      </div>

      {/* Connection Status */}
      <div className={cn(
        "card p-4 flex items-center justify-between",
        isConnected
          ? "border-green-200 dark:border-green-800 bg-success-50 dark:bg-success-900/20"
          : "border-red-200 dark:border-red-800 bg-danger-50 dark:bg-danger-900/20"
      )}>
        <div className="flex items-center gap-3">
          {isConnected ? (
            <CheckCircle className="w-6 h-6 text-success-600 dark:text-success-400" />
          ) : (
            <XCircle className="w-6 h-6 text-danger-600 dark:text-danger-400" />
          )}
          <div>
            <p className={cn(
              "font-medium",
              isConnected ? "text-green-800 dark:text-green-200" : "text-red-800 dark:text-red-200"
            )}>
              {isConnected ? 'Conectado a Brevo' : 'No conectado'}
            </p>
            {isConnected && statusQuery.data?.email && (
              <p className="text-sm text-success-600 dark:text-success-400">
                {statusQuery.data.email} - {statusQuery.data.company_name}
              </p>
            )}
            {!isConnected && statusQuery.data?.error && (
              <p className="text-sm text-danger-600 dark:text-danger-400">
                {statusQuery.data.error}
              </p>
            )}
          </div>
        </div>
        {statusQuery.isLoading && (
          <RefreshCw className="w-5 h-5 animate-spin text-slate-500" />
        )}
      </div>

      {/* Main Content */}
      {isConnected && (
        <>
          {/* Segment Mappings */}
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Link2 className="w-5 h-5 text-primary-600" />
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Mapeo de Segmentos RFM a Listas Brevo
                </h2>
              </div>
              <button
                onClick={() => syncAllMutation.mutate()}
                disabled={syncAllMutation.isPending}
                className="btn btn-secondary btn-sm"
              >
                <RefreshCw className={cn("w-4 h-4 mr-2", syncAllMutation.isPending && "animate-spin")} />
                Sincronizar Todos
              </button>
            </div>

            <div className="card-body">
              {configQuery.isLoading ? (
                <div className="text-center py-8 text-slate-500">Cargando mapeos...</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {configQuery.data?.mappings.map((mapping: SegmentMapping) => {
                    const style = getSegmentStyle(mapping.segment_name)
                    const Icon = style.icon

                    return (
                      <div
                        key={mapping.segment_name}
                        className={cn(
                          "p-4 rounded-lg border transition-all",
                          style.bgLight,
                          "dark:" + style.bgDark,
                          "border-slate-200 dark:border-slate-700"
                        )}
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <Icon className={cn("w-5 h-5", style.iconColor)} />
                          <h3 className="font-medium text-slate-900 dark:text-white">
                            {mapping.segment_name}
                          </h3>
                        </div>

                        {mapping.brevo_list_id ? (
                          <>
                            <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                              Lista: <span className="font-medium">{mapping.brevo_list_name}</span>
                            </p>
                            <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 mb-3">
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {mapping.last_sync_count} contactos
                              </span>
                              {mapping.last_sync_at && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {new Date(mapping.last_sync_at).toLocaleDateString('es-ES')}
                                </span>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => syncSegmentMutation.mutate(mapping.segment_name)}
                                disabled={syncSegmentMutation.isPending}
                                className="btn btn-secondary btn-xs flex-1"
                              >
                                <RefreshCw className={cn(
                                  "w-3 h-3 mr-1",
                                  syncSegmentMutation.isPending && "animate-spin"
                                )} />
                                Sync
                              </button>
                              <button
                                onClick={() => handleCreateCampaign(mapping.segment_name)}
                                className="btn btn-primary btn-xs flex-1"
                              >
                                <Mail className="w-3 h-3 mr-1" />
                                Enviar
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="text-center py-2">
                            <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">
                              Sin lista asignada
                            </p>
                            <button
                              onClick={() => setShowConfigModal(true)}
                              className="btn btn-secondary btn-xs"
                            >
                              <ListPlus className="w-3 h-3 mr-1" />
                              Asignar Lista
                            </button>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Campaign History */}
          <div className="card">
            <div className="card-header flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary-600" />
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Campanas Enviadas
              </h2>
              {campaignsQuery.data?.count !== undefined && (
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  ({campaignsQuery.data.count})
                </span>
              )}
            </div>

            <div className="card-body p-0">
              {!isConnected ? (
                <div className="text-center py-8 text-slate-500">Conecta con Brevo para ver campanas</div>
              ) : campaignsQuery.isLoading ? (
                <div className="text-center py-8 text-slate-500">Cargando campanas...</div>
              ) : campaignsQuery.data && campaignsQuery.data.campaigns.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 dark:bg-slate-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                          Campana
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                          Enviados
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                          Entregados
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                          Abiertos
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                          Clicks
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                          Fecha
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                          Estado
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                      {campaignsQuery.data.campaigns.map((campaign) => (
                        <tr key={campaign.id} className="hover:bg-slate-50 dark:hover:bg-gray-800/50">
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-slate-900 dark:text-white">
                                {campaign.name}
                              </p>
                              <p className="text-sm text-slate-500 dark:text-slate-400 truncate max-w-xs">
                                {campaign.subject}
                              </p>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center text-sm text-slate-900 dark:text-white">
                            {campaign.statistics?.sent ?? '-'}
                          </td>
                          <td className="px-4 py-3 text-center text-sm text-slate-900 dark:text-white">
                            {campaign.statistics?.delivered ?? '-'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-sm text-slate-900 dark:text-white">
                              {campaign.statistics?.opened ?? '-'}
                            </span>
                            {campaign.statistics && campaign.statistics.delivered > 0 && (
                              <span className="text-xs text-slate-500 ml-1">
                                ({Math.round((campaign.statistics.opened / campaign.statistics.delivered) * 100)}%)
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-sm text-slate-900 dark:text-white">
                              {campaign.statistics?.clicked ?? '-'}
                            </span>
                            {campaign.statistics && campaign.statistics.delivered > 0 && (
                              <span className="text-xs text-slate-500 ml-1">
                                ({Math.round((campaign.statistics.clicked / campaign.statistics.delivered) * 100)}%)
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                            {campaign.sent_date
                              ? new Date(campaign.sent_date).toLocaleString('es-ES', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                              : '-'
                            }
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={cn(
                              "px-2 py-1 rounded-full text-xs font-medium",
                              campaign.status === 'sent'
                                ? "bg-success-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                : campaign.status === 'scheduled'
                                ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
                                : "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-400"
                            )}>
                              {campaign.status === 'sent' ? 'Enviada' :
                               campaign.status === 'scheduled' ? 'Programada' :
                               campaign.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No hay campanas enviadas todavia</p>
                  <button
                    onClick={() => handleCreateCampaign()}
                    className="btn btn-primary btn-sm mt-4"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Crear Primera Campana
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Not Connected State */}
      {!isConnected && !statusQuery.isLoading && (
        <div className="card p-8 text-center">
          <Mail className="w-16 h-16 mx-auto mb-4 text-slate-400" />
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
            Configura Brevo para empezar
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            Conecta tu cuenta de Brevo para enviar campanas de email marketing a tus segmentos de clientes
          </p>
          <button
            onClick={() => setShowConfigModal(true)}
            className="btn btn-primary"
          >
            <Settings className="w-4 h-4 mr-2" />
            Configurar Brevo
          </button>
        </div>
      )}

      {/* Modals */}
      {showConfigModal && (
        <BrevoConfigModal
          isOpen={showConfigModal}
          onClose={() => setShowConfigModal(false)}
          lists={listsQuery.data || []}
          mappings={configQuery.data?.mappings || []}
        />
      )}

      {showCampaignModal && (
        <CampaignModal
          isOpen={showCampaignModal}
          onClose={() => {
            setShowCampaignModal(false)
            setSelectedSegmentForCampaign(null)
          }}
          lists={listsQuery.data || []}
          mappings={configQuery.data?.mappings || []}
          preselectedSegment={selectedSegmentForCampaign}
        />
      )}
    </div>
  )
}
